import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// The date-tab state every report page shares (Today / Week / Month / Year /
// Custom + the two custom pickers and their validation). Lifted out of the
// patient report / count pages' inline copies so the three platform report
// pages (dashboard, tenant report, tenant count) do not carry a fourth, fifth
// and sixth copy of the same 120 lines. The original two pages are untouched.
//
// `calendar` picks how a preset tab resolves:
//   false — rolling windows ending today (last 7 days, last month, last year),
//           the Patients Report convention.
//   true  — the current calendar week (Mon–Sun) / month / year, the Patient
//           Count convention, so a chart's buckets line up with the calendar.

export const REPORT_TABS = ['today', 'week', 'month', 'custom'];
export const DASHBOARD_TABS = ['today', 'week', 'month', 'year', 'custom'];

const EMPTY_ERRORS = { startDate: '', endDate: '' };

// Mirrors MAX_RANGE_DAYS in Backend/services/platformReportsUtil.js — the
// platform report endpoints answer 400 past this, so the picker refuses first.
// Both sides count inclusively (rangeLengthInDays below === the API's dayCount).
export const MAX_RANGE_DAYS = 731;

// YYYY-MM-DD in the browser's local time zone (toISOString would shift the day
// for anyone east of UTC late in the evening).
export const formatDateToLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const getReportDateRange = (tab, { startDate = '', endDate = '', calendar = false } = {}) => {
    const today = new Date();
    const todayStr = formatDateToLocal(today);

    switch (tab) {
        case 'today':
            return { from: todayStr, to: todayStr };
        case 'week': {
            if (calendar) {
                const currentDay = today.getDay(); // 0 = Sunday
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - currentDay + (currentDay === 0 ? -6 : 1));
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                return { from: formatDateToLocal(weekStart), to: formatDateToLocal(weekEnd) };
            }
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            return { from: formatDateToLocal(weekAgo), to: todayStr };
        }
        case 'month': {
            if (calendar) {
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                return { from: formatDateToLocal(monthStart), to: formatDateToLocal(monthEnd) };
            }
            const monthAgo = new Date(today);
            monthAgo.setMonth(today.getMonth() - 1);
            return { from: formatDateToLocal(monthAgo), to: todayStr };
        }
        case 'year': {
            if (calendar) {
                return {
                    from: formatDateToLocal(new Date(today.getFullYear(), 0, 1)),
                    to: formatDateToLocal(new Date(today.getFullYear(), 11, 31)),
                };
            }
            const yearAgo = new Date(today);
            yearAgo.setFullYear(today.getFullYear() - 1);
            return { from: formatDateToLocal(yearAgo), to: todayStr };
        }
        case 'custom':
            return { from: startDate, to: endDate };
        default:
            return { from: todayStr, to: todayStr };
    }
};

// Whole days between two YYYY-MM-DD strings, inclusive of both ends.
export const rangeLengthInDays = (from, to) => {
    const a = new Date(from);
    const b = new Date(to);
    if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
    return Math.floor((b - a) / 86400000) + 1;
};

// A backend `period` key ('YYYY-MM-DD' for day buckets, 'YYYY-MM' for month)
// as a LOCAL Date, or null. Date-only ISO strings parse as UTC midnight, which
// toLocaleDateString then shifts a day back for anyone west of UTC; the
// T00:00:00 form parses as local time, so the label matches the key in every
// zone. Anything already carrying a time part is parsed as-is.
export const parsePeriod = (period) => {
    if (period === null || period === undefined) return null;
    const s = String(period);
    let d;
    if (/^\d{4}-\d{2}$/.test(s)) d = new Date(`${s}-01T00:00:00`);
    else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) d = new Date(`${s}T00:00:00`);
    else d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

const validateReportDates = (start, end, t) => {
    const errors = { ...EMPTY_ERRORS };
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (start) {
        const startDateObj = new Date(start);
        startDateObj.setHours(0, 0, 0, 0);
        if (isNaN(startDateObj.getTime())) {
            errors.startDate = t('reportsCommon.invalid_date_format', 'Invalid date format.');
        }
        if (end) {
            const endDateObj = new Date(end);
            endDateObj.setHours(0, 0, 0, 0);
            if (startDateObj > endDateObj) {
                errors.startDate = t('reportsCommon.start_after_end', 'Start date cannot be after end date.');
            }
        }
    }

    if (end) {
        const endDateObj = new Date(end);
        endDateObj.setHours(0, 0, 0, 0);
        if (isNaN(endDateObj.getTime())) {
            errors.endDate = t('reportsCommon.invalid_date_format', 'Invalid date format.');
        }
        if (endDateObj > today) {
            errors.endDate = t('reportsCommon.end_in_future', 'End date cannot be in the future.');
        }
        if (start) {
            const startDateObj = new Date(start);
            startDateObj.setHours(0, 0, 0, 0);
            if (endDateObj < startDateObj) {
                errors.endDate = t('reportsCommon.end_before_start', 'End date cannot be before start date.');
            }
        }
    }

    if (end && !start) {
        errors.startDate = t('reportsCommon.start_required', 'Start date is required.');
    }

    if (start && end && !errors.startDate && !errors.endDate
        && rangeLengthInDays(start, end) > MAX_RANGE_DAYS) {
        errors.endDate = t('reportsCommon.range_too_long', 'Date range cannot exceed {{days}} days.', { days: MAX_RANGE_DAYS });
    }

    return errors;
};

export default function useReportDateRange({ defaultTab = 'month', calendar = false } = {}) {
    const { t } = useTranslation();
    const [activeTab, setActiveTabState] = useState(defaultTab);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateErrors, setDateErrors] = useState(EMPTY_ERRORS);

    const validate = (start, end) => {
        const errors = validateReportDates(start, end, t);
        setDateErrors(errors);
        return Object.values(errors).every((e) => !e);
    };

    const reset = () => {
        setStartDate('');
        setEndDate('');
        setDateErrors(EMPTY_ERRORS);
    };

    // Switching to Custom starts from blank pickers, as the report pages do.
    // Re-clicking the already-active Custom pill must not wipe chosen dates.
    const setActiveTab = (tab) => {
        if (tab === 'custom' && tab !== activeTab) reset();
        setActiveTabState(tab);
    };

    const changeStartDate = (value) => {
        setStartDate(value);
        if (value || endDate) validate(value, endDate);
        else setDateErrors(EMPTY_ERRORS);
    };

    const changeEndDate = (value) => {
        setEndDate(value);
        if (value || startDate) validate(startDate, value);
        else setDateErrors(EMPTY_ERRORS);
    };

    // Custom needs both pickers filled before there is anything to fetch or
    // show — the pages render an empty table / chart until then.
    const ready = activeTab !== 'custom' || Boolean(startDate && endDate);

    /**
     * The { from, to } to query, or null when there is nothing valid to ask
     * for yet. Warns (toast) when asked to and the custom range is incomplete.
     */
    const resolveRange = ({ warn = false } = {}) => {
        const { from, to } = getReportDateRange(activeTab, { startDate, endDate, calendar });
        if (!from || !to) {
            if (activeTab === 'custom' && warn) {
                toast.warning(t('reportsCommon.select_both_dates', 'Please select both start and end dates.'));
            }
            return null;
        }
        if (activeTab === 'custom' && !validate(startDate, endDate)) {
            if (warn) toast.warning(t('reportsCommon.select_valid_date_range', 'Please select a valid date range.'));
            return null;
        }
        return { from, to };
    };

    return {
        activeTab, setActiveTab,
        startDate, endDate, dateErrors,
        changeStartDate, changeEndDate, reset,
        ready, resolveRange,
    };
}
